// Story saas-PDF-C: pure aggregators per FieldType.
// Testable unit — zero DOM, zero side effects.
// Output shapes feed diretamente em components/charts em reportCharts.tsx.

import type {
  FormField,
  FormResponse,
  OpinionScaleConfig,
  RatingConfig,
} from '../../types/forms';

// ─── Single choice (select / radio) ──────────────────────────────

export interface SingleChoiceBucket {
  label: string;
  count: number;
  percentage: number;
}

export function aggregateSingleChoice(
  responses: FormResponse[],
  field: FormField
): SingleChoiceBucket[] {
  const counts = new Map<string, number>();
  let total = 0;

  for (const r of responses) {
    const raw = r.data?.[field.id];
    if (raw === null || raw === undefined || raw === '') continue;
    const value = String(raw);
    counts.set(value, (counts.get(value) ?? 0) + 1);
    total += 1;
  }

  const buckets: SingleChoiceBucket[] = [];
  for (const [value, count] of counts.entries()) {
    const label = resolveOptionLabel(field, value);
    buckets.push({
      label,
      count,
      percentage: total === 0 ? 0 : Math.round((count / total) * 1000) / 10,
    });
  }

  // Sort by count desc for consistent chart ordering.
  buckets.sort((a, b) => b.count - a.count);
  return buckets;
}

// ─── Multi choice (checkbox / multiselect / picture_choice multi) ──

export function aggregateMultiChoice(
  responses: FormResponse[],
  field: FormField
): SingleChoiceBucket[] {
  const counts = new Map<string, number>();
  let totalResponses = 0;

  for (const r of responses) {
    const raw = r.data?.[field.id];
    const values = Array.isArray(raw) ? raw : raw !== null && raw !== undefined && raw !== '' ? [raw] : [];
    if (values.length === 0) continue;
    totalResponses += 1;
    for (const v of values) {
      const key = String(v);
      counts.set(key, (counts.get(key) ?? 0) + 1);
    }
  }

  const buckets: SingleChoiceBucket[] = [];
  for (const [value, count] of counts.entries()) {
    const label = resolveOptionLabel(field, value);
    buckets.push({
      label,
      count,
      // % of responses that selected this option (can sum to >100% — expected for multi).
      percentage: totalResponses === 0 ? 0 : Math.round((count / totalResponses) * 1000) / 10,
    });
  }

  buckets.sort((a, b) => b.count - a.count);
  return buckets;
}

// ─── Yes/No ──────────────────────────────────────────────────────

export interface YesNoAgg {
  yes: number;
  no: number;
  total: number;
}

export function aggregateYesNo(responses: FormResponse[], field: FormField): YesNoAgg {
  let yes = 0;
  let no = 0;
  for (const r of responses) {
    const v = r.data?.[field.id];
    if (v === null || v === undefined || v === '') continue;
    if (v === true || v === 'yes' || v === 'true' || v === 'sim') yes += 1;
    else no += 1;
  }
  return { yes, no, total: yes + no };
}

// ─── NPS (0-10 scale) ────────────────────────────────────────────

export interface NPSAgg {
  /** NPS score ∈ [-100, +100], inteiro. */
  score: number;
  detractors: number;
  neutrals: number;
  promoters: number;
  total: number;
}

export function aggregateNPS(responses: FormResponse[], field: FormField): NPSAgg {
  let detractors = 0;
  let neutrals = 0;
  let promoters = 0;

  for (const r of responses) {
    const raw = r.data?.[field.id];
    if (raw === null || raw === undefined || raw === '') continue;
    const n = Number(raw);
    if (Number.isNaN(n) || n < 0 || n > 10) continue;
    if (n <= 6) detractors += 1;
    else if (n <= 8) neutrals += 1;
    else promoters += 1;
  }

  const total = detractors + neutrals + promoters;
  const score =
    total === 0 ? 0 : Math.round(((promoters - detractors) / total) * 100);

  return { score, detractors, neutrals, promoters, total };
}

// ─── Opinion scale ───────────────────────────────────────────────

export interface ScaleBucket {
  value: number;
  count: number;
}

export function aggregateOpinionScale(
  responses: FormResponse[],
  field: FormField
): ScaleBucket[] {
  const cfg = (field.config as OpinionScaleConfig | undefined) || {};
  const min = cfg.minValue ?? 1;
  const max = cfg.maxValue ?? 10;

  const bucketMap = new Map<number, number>();
  for (let v = min; v <= max; v++) bucketMap.set(v, 0);

  for (const r of responses) {
    const raw = r.data?.[field.id];
    if (raw === null || raw === undefined || raw === '') continue;
    const n = Number(raw);
    if (Number.isNaN(n)) continue;
    if (bucketMap.has(n)) {
      bucketMap.set(n, (bucketMap.get(n) ?? 0) + 1);
    }
  }

  const buckets: ScaleBucket[] = [];
  for (const [value, count] of bucketMap.entries()) {
    buckets.push({ value, count });
  }
  buckets.sort((a, b) => a.value - b.value);
  return buckets;
}

// ─── Rating (stars / heart / emoji) ──────────────────────────────

export function aggregateRating(responses: FormResponse[], field: FormField): ScaleBucket[] {
  const cfg = (field.config as RatingConfig | undefined) || {};
  const max = cfg.maxRating ?? 5;

  const bucketMap = new Map<number, number>();
  for (let v = 1; v <= max; v++) bucketMap.set(v, 0);

  for (const r of responses) {
    const raw = r.data?.[field.id];
    if (raw === null || raw === undefined || raw === '') continue;
    const n = Number(raw);
    if (Number.isNaN(n)) continue;
    if (bucketMap.has(n)) {
      bucketMap.set(n, (bucketMap.get(n) ?? 0) + 1);
    }
  }

  const buckets: ScaleBucket[] = [];
  for (const [value, count] of bucketMap.entries()) {
    buckets.push({ value, count });
  }
  buckets.sort((a, b) => a.value - b.value);
  return buckets;
}

// ─── Number stats ────────────────────────────────────────────────

export interface NumberStats {
  min: number;
  max: number;
  avg: number;
  median: number;
  count: number;
}

export function aggregateNumber(responses: FormResponse[], field: FormField): NumberStats {
  const values: number[] = [];
  for (const r of responses) {
    const raw = r.data?.[field.id];
    if (raw === null || raw === undefined || raw === '') continue;
    const n = Number(raw);
    if (!Number.isNaN(n) && Number.isFinite(n)) values.push(n);
  }

  if (values.length === 0) {
    return { min: 0, max: 0, avg: 0, median: 0, count: 0 };
  }

  values.sort((a, b) => a - b);
  const count = values.length;
  const min = values[0];
  const max = values[count - 1];
  const sum = values.reduce((acc, v) => acc + v, 0);
  const avg = Math.round((sum / count) * 100) / 100;
  const median =
    count % 2 === 0
      ? (values[count / 2 - 1] + values[count / 2]) / 2
      : values[Math.floor(count / 2)];

  return { min, max, avg, median: Math.round(median * 100) / 100, count };
}

// ─── Date distribution ───────────────────────────────────────────

export interface DateBucket {
  period: string;
  count: number;
}

export function aggregateDate(responses: FormResponse[], field: FormField): DateBucket[] {
  const parsed: Date[] = [];
  for (const r of responses) {
    const raw = r.data?.[field.id];
    if (!raw) continue;
    const d = new Date(String(raw));
    if (!Number.isNaN(d.getTime())) parsed.push(d);
  }

  if (parsed.length === 0) return [];

  parsed.sort((a, b) => a.getTime() - b.getTime());
  const rangeDays =
    (parsed[parsed.length - 1].getTime() - parsed[0].getTime()) / (1000 * 60 * 60 * 24);

  const granularity: 'day' | 'month' = rangeDays < 30 ? 'day' : 'month';
  const counts = new Map<string, number>();

  for (const d of parsed) {
    const key =
      granularity === 'day'
        ? `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
        : `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
    counts.set(key, (counts.get(key) ?? 0) + 1);
  }

  const buckets: DateBucket[] = [];
  for (const [period, count] of counts.entries()) buckets.push({ period, count });
  buckets.sort((a, b) => a.period.localeCompare(b.period));
  return buckets;
}

// ─── Sampling (PC.4) ─────────────────────────────────────────────

export interface SampleResult<T> {
  sampled: T[];
  wasSampled: boolean;
  originalCount: number;
}

export function sampleResponses(
  responses: FormResponse[],
  max = 1000
): SampleResult<FormResponse> {
  const originalCount = responses.length;
  if (originalCount <= max) {
    return { sampled: responses, wasSampled: false, originalCount };
  }
  // Top N mais recentes por created_at DESC
  const sorted = [...responses].sort(
    (a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
  );
  return { sampled: sorted.slice(0, max), wasSampled: true, originalCount };
}

// ─── Anonimization (PC.7 LGPD) ───────────────────────────────────

const IDENTIFIER_FIELD_NAMES = new Set([
  'email',
  'e-mail',
  'e_mail',
  'mail',
  'nome',
  'name',
  'full_name',
  'fullname',
  'fullName',
  'full-name',
]);

function isIdentifierField(field: FormField): boolean {
  if (field.type === 'email') return true;
  const label = (field.label || '').toLowerCase().trim();
  const labelKey = label.replace(/\s+/g, '_');
  if (IDENTIFIER_FIELD_NAMES.has(labelKey)) return true;
  // Common BR patterns
  if (label.includes('email')) return true;
  if (label === 'nome' || label === 'nome completo' || label === 'seu nome') return true;
  return false;
}

/**
 * Returns a shallow-cloned response with identifier fields replaced.
 * Keeps the original object untouched (pure function).
 * - email fields → '***@***'
 * - name/nome fields → 'Respondente #N' (N = index+1)
 */
export function anonymizeResponse(
  response: FormResponse,
  fields: FormField[],
  index: number
): FormResponse {
  const newData: Record<string, unknown> = { ...(response.data ?? {}) };
  const placeholder = `Respondente #${index + 1}`;

  for (const f of fields) {
    if (!isIdentifierField(f)) continue;
    const current = newData[f.id];
    if (current === null || current === undefined || current === '') continue;
    if (f.type === 'email' || (typeof current === 'string' && current.includes('@'))) {
      newData[f.id] = '***@***';
    } else {
      newData[f.id] = placeholder;
    }
  }

  return { ...response, data: newData };
}

// ─── Helpers ─────────────────────────────────────────────────────

function resolveOptionLabel(field: FormField, value: string): string {
  const options = field.options || [];
  for (const opt of options) {
    if (typeof opt === 'string') {
      if (opt === value) return opt;
    } else if (opt && typeof opt === 'object' && 'value' in opt) {
      if ((opt as { value: string }).value === value) return (opt as { label: string }).label;
    }
  }
  // picture_choice uses image URL or label as value — fallback to value as-is
  return value;
}

/**
 * Type-aware dispatcher — returns the aggregation shape for a given field type.
 * Used by ReportPDFTemplate to pick the right chart component.
 */
export type FieldAggregation =
  | { kind: 'single_choice'; data: SingleChoiceBucket[] }
  | { kind: 'multi_choice'; data: SingleChoiceBucket[] }
  | { kind: 'yes_no'; data: YesNoAgg }
  | { kind: 'nps'; data: NPSAgg }
  | { kind: 'opinion_scale'; data: ScaleBucket[] }
  | { kind: 'rating'; data: ScaleBucket[] }
  | { kind: 'number'; data: NumberStats }
  | { kind: 'date'; data: DateBucket[] }
  | { kind: 'unsupported'; data: null };

export function aggregateField(
  responses: FormResponse[],
  field: FormField
): FieldAggregation {
  switch (field.type) {
    case 'select':
    case 'radio':
      return { kind: 'single_choice', data: aggregateSingleChoice(responses, field) };
    case 'checkbox':
    case 'multiselect':
    case 'picture_choice':
      return { kind: 'multi_choice', data: aggregateMultiChoice(responses, field) };
    case 'yes_no':
      return { kind: 'yes_no', data: aggregateYesNo(responses, field) };
    case 'nps':
      return { kind: 'nps', data: aggregateNPS(responses, field) };
    case 'opinion_scale':
      return { kind: 'opinion_scale', data: aggregateOpinionScale(responses, field) };
    case 'rating':
      return { kind: 'rating', data: aggregateRating(responses, field) };
    case 'number':
      return { kind: 'number', data: aggregateNumber(responses, field) };
    case 'date':
    case 'datetime':
      return { kind: 'date', data: aggregateDate(responses, field) };
    default:
      return { kind: 'unsupported', data: null };
  }
}

/**
 * Field types that can be aggregated into charts.
 * Used by ReportConfigModal step 2 to list selectable fields.
 */
export const AGGREGATABLE_FIELD_TYPES = [
  'select',
  'radio',
  'checkbox',
  'multiselect',
  'picture_choice',
  'yes_no',
  'nps',
  'opinion_scale',
  'rating',
  'number',
  'date',
  'datetime',
] as const;

export function isAggregatable(field: FormField): boolean {
  return (AGGREGATABLE_FIELD_TYPES as readonly string[]).includes(field.type);
}

/**
 * Field types that feed the appendix (raw text dump).
 */
export const APPENDIX_FIELD_TYPES = ['text', 'textarea'] as const;

export function isAppendixField(field: FormField): boolean {
  return (APPENDIX_FIELD_TYPES as readonly string[]).includes(field.type);
}
